import { useState } from "react";
import { UserPlusIcon } from "lucide-react";
import { Button } from "@component/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { TrainingCsvUploader } from "@component/training/training-csv-uploader";
import { addEmployeesToCampaign } from "@service/training";
import type { EmployeeRow } from "@type/training";

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    campaignId: string;
    onAdded: (added: number, skipped: number) => void;
}

export function AddEmployeesDialog({
    open,
    onOpenChange,
    campaignId,
    onAdded,
}: Props) {
    const [rows, setRows] = useState<EmployeeRow[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleClose = (next: boolean) => {
        if (submitting) return;
        if (!next) {
            setRows([]);
            setError(null);
        }
        onOpenChange(next);
    };

    const handleConfirm = async () => {
        if (rows.length === 0) return;
        setSubmitting(true);
        setError(null);
        try {
            // TODO: BE needs to accept team_leader on POST /training/campaigns/:id/employees
            const result = await addEmployeesToCampaign(campaignId, rows);
            if (!result) {
                setError(
                    "We couldn't add these employees. Please try again."
                );
                return;
            }
            onAdded(result.added, result.skipped_duplicates);
            setRows([]);
            onOpenChange(false);
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "We couldn't add these employees. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Add employees to this campaign</DialogTitle>
                    <DialogDescription className="pt-2 text-sm text-gray-600">
                        Add employees manually or import from a CSV file. Anyone
                        already invited to this campaign will be skipped automatically.
                    </DialogDescription>
                </DialogHeader>
                <div className="py-2">
                    <TrainingCsvUploader
                        onParsed={setRows}
                        showManual
                    />
                </div>

                {error && (
                    <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">
                        {error}
                    </div>
                )}
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => handleClose(false)}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={submitting || rows.length === 0}
                    >
                        <UserPlusIcon className="w-4 h-4" />
                        {submitting
                            ? "Adding..."
                            : rows.length > 0
                              ? `Add ${rows.length} employee${rows.length > 1 ? "s" : ""}`
                              : "Add employees"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
